import React, { useState, useEffect } from 'react';
import { Search, X } from 'lucide-react';

export const SearchButton = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Search content when query changes
  useEffect(() => {
    if (searchQuery.trim().length > 2) {
      performSearch(searchQuery);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const performSearch = async (query) => {
    setIsSearching(true);
    const lowerQuery = query.toLowerCase();
    const results = [];

    try {
      // Search FAQs
      const faqRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/faqs`);
      const faqs = await faqRes.json();
      faqs.forEach(faq => {
        if (faq.question.toLowerCase().includes(lowerQuery) || 
            faq.answer.toLowerCase().includes(lowerQuery)) {
          results.push({
            type: 'FAQ',
            title: faq.question,
            snippet: faq.answer.substring(0, 100) + '...',
            action: () => {
              setIsSearchOpen(false);
              document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
            }
          });
        }
      });

      // Search Partners
      const partnerRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/partners`);
      const partners = await partnerRes.json();
      partners.forEach(partner => {
        if (partner.name.toLowerCase().includes(lowerQuery) || 
            partner.category.toLowerCase().includes(lowerQuery) ||
            partner.services?.some(s => s.toLowerCase().includes(lowerQuery))) {
          results.push({
            type: 'Partner',
            title: partner.name,
            snippet: partner.category,
            action: () => {
              setIsSearchOpen(false);
              document.getElementById('partners')?.scrollIntoView({ behavior: 'smooth' });
            }
          });
        }
      });

      // Add static content matches
      const staticContent = [
        { keywords: ['service', 'relocation', 'travel', 'transport'], title: 'Pet Relocation Services', section: 'services' },
        { keywords: ['import', 'checklist', 'requirements', 'documents'], title: 'Import Checklist', section: 'import-checklist', isPage: true },
        { keywords: ['travelsafe', 'insurance', 'protection', 'coverage'], title: 'TravelSafe Service', section: 'travelsafe', isPage: true },
        { keywords: ['contact', 'consultation', 'help', 'support'], title: 'Contact Us', section: 'contact' },
        { keywords: ['pricing', 'cost', 'fees', 'price'], title: 'Pricing Information', section: 'pricing' },
        { keywords: ['about', 'team', 'veterinary', 'leadership'], title: 'About Us', section: 'about' },
      ];

      staticContent.forEach(content => {
        if (content.keywords.some(kw => kw.includes(lowerQuery) || lowerQuery.includes(kw))) {
          results.push({
            type: 'Page Section',
            title: content.title,
            snippet: `Navigate to ${content.title}`,
            action: () => {
              setIsSearchOpen(false);
              if (content.isPage) {
                window.location.href = `/${content.section}`;
              } else {
                document.getElementById(content.section)?.scrollIntoView({ behavior: 'smooth' });
              }
            }
          });
        }
      });

      setSearchResults(results.slice(0, 8)); // Limit to 8 results
    } catch (error) {
      console.error('Search error:', error);
    }
    setIsSearching(false);
  };

  const handleQuickLink = (section) => {
    setIsSearchOpen(false);
    const sectionId = section.toLowerCase().replace(' ', '-');
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      if (section === 'Import Checklist') {
        window.location.href = '/import-checklist';
      } else if (section === 'TravelSafe') {
        window.location.href = '/travelsafe';
      }
    }
  };

  return (
    <>
      {/* Floating Search Button */}
      <button
        onClick={() => setIsSearchOpen(true)}
        className="fixed bottom-20 right-24 z-50 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 group"
        aria-label="Search website"
      >
        <Search className="w-7 h-7" />
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Search our site
        </span>
      </button>

      {/* Search Modal */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-start justify-center pt-20 px-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full animate-in fade-in slide-in-from-top-4 duration-300">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-foreground flex items-center gap-2">
                <Search className="w-6 h-6 text-primary" />
                Search Pawsome Ventures
              </h3>
              <button
                onClick={() => {
                  setIsSearchOpen(false);
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close search"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Search Input & Results */}
            <div className="p-6">
              <div className="relative mb-6">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for services, FAQs, partners..."
                  className="w-full pl-12 pr-4 py-4 border-2 border-primary/20 rounded-xl text-lg focus:outline-none focus:border-primary transition-colors"
                  autoFocus
                />
              </div>

              {/* Search Results */}
              {searchQuery.length > 2 && (
                <div className="mb-6">
                  {isSearching ? (
                    <div className="text-center py-8 text-gray-500">
                      <Search className="w-8 h-8 mx-auto mb-2 animate-pulse" />
                      Searching...
                    </div>
                  ) : searchResults.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      <p className="text-sm text-gray-600 mb-3 font-semibold">
                        Found {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}:
                      </p>
                      {searchResults.map((result, index) => (
                        <button
                          key={index}
                          onClick={result.action}
                          className="w-full text-left p-4 bg-gray-50 hover:bg-primary/10 rounded-xl transition-colors border border-gray-200 hover:border-primary/30"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs font-semibold text-primary bg-primary/10 px-2 py-1 rounded">
                                  {result.type}
                                </span>
                              </div>
                              <h4 className="font-semibold text-foreground mb-1">{result.title}</h4>
                              <p className="text-sm text-gray-600">{result.snippet}</p>
                            </div>
                            <Search className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                      No results found for "{searchQuery}"
                    </div>
                  )}
                </div>
              )}

              {/* Divider */}
              {searchQuery.length <= 2 && (
                <div className="border-t border-gray-200 pt-6">
                  <p className="text-center text-gray-600 mb-6">
                    Type to search or use quick navigation below
                  </p>
                </div>
              )}

              {/* Quick Links */}
              <div className={searchQuery.length > 2 ? 'mt-6 pt-6 border-t border-gray-200' : ''}>
                <p className="text-sm text-gray-500 font-semibold mb-3">Quick Navigation:</p>
                <div className="grid grid-cols-2 gap-3">
                  {['Services', 'Import Checklist', 'TravelSafe', 'FAQ', 'Partners', 'Contact'].map((link) => (
                    <button
                      key={link}
                      type="button"
                      onClick={() => handleQuickLink(link)}
                      className="px-4 py-3 bg-gradient-to-r from-primary/10 to-accent/10 hover:from-primary/20 hover:to-accent/20 text-primary rounded-xl text-sm font-semibold hover:scale-105 transition-all border border-primary/20 hover:border-primary/40"
                    >
                      {link}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
